import logout from "~/utils/logout";

// Module-level state so parallel 401s share a single refresh attempt and
// only one redirect to /login fires no matter how many requests failed.
let refreshPromise = null;
let redirectingToLogin = false;

function doRefresh($axios, app) {
  if (refreshPromise) return refreshPromise;

  refreshPromise = $axios({
    method: "POST",
    url: `${app.$config.clientURL}/api/getToken`,
    headers: { "Content-Type": "application/json" },
    progress: false,
    withCredentials: true,
    // refresh_token is intentionally omitted — the Express /api/getToken
    // endpoint reads the HttpOnly refreshToken cookie from the request.
    data: { grant_type: "refresh_token" },
  })
    .then((response) => {
      let data = response && response.data && response.data.data;
      data = typeof data === "string" ? JSON.parse(data) : data;
      if (!data || !data.access_token || !data.refresh_token) {
        throw new Error("refresh_failed");
      }
      const tokenType = data.token_type || "Bearer";
      $axios.setToken(data.access_token, tokenType);
      $axios.setHeader("Authorization", `${tokenType} ${data.access_token}`);
      // A successful refresh proves the session is healthy again — allow
      // future 401 bursts to redirect normally if they can't recover.
      redirectingToLogin = false;
      return data;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

export default function ({ $axios, app, redirect, store }) {
  $axios.onRequest((config) => {
    config.progress = false;
    config.withCredentials = true;
    // Forward impersonation session cookie to backend (SSR doesn't
    // auto-forward browser cookies to cross-origin axios calls).
    try {
      const sid = app && app.$cookies ? app.$cookies.get("impersonation_sid") : null;
      if (sid) {
        const existing = (config.headers.Cookie || config.headers.cookie || "").trim();
        const newCookie = "impersonation_sid=" + sid;
        config.headers.Cookie = existing ? existing + "; " + newCookie : newCookie;
      }
    } catch (e) { /* ignore */ }
  });

  let accessToken = store.state.Auth.access_token;
  if (!process.browser) {
    accessToken = app.$cookies.get("accessToken");
  }

  if (accessToken) {
    $axios.setHeader("Authorization", `Bearer ${accessToken}`);
    $axios.setToken(accessToken, "Bearer");
  }

  $axios.onError(async (error) => {
    const reqStatus = error.request && error.request.status;
    const resStatus = error.response && error.response.status;
    const isAuthError =
      reqStatus === 400 || reqStatus === 401 ||
      resStatus === 400 || resStatus === 401;

    if (!isAuthError) return;

    // Impersonation sessions authenticate via a separate session cookie;
    // they never participate in the Bearer-token refresh dance.
    try {
      if (app.$cookies && app.$cookies.get("impersonation_info")) return;
    } catch (e) { /* ignore */ }

    const url = (error.config && error.config.url) || "";
    const excluded =
      url.includes("/api/getToken") ||
      url.includes("/api/logout") ||
      url.includes("/rating") ||
      url.includes("/user/lost-password-reset") ||
      url.includes("/user/lost-password-pardot") ||
      url.includes("/tools/resposible/reports") ||
      url.includes("/auth/impersonate/");

    if (excluded) {
      if (
        url.includes("/user/lost-password-reset") ||
        url.includes("/user/lost-password")
      ) {
        return error.response;
      }
      return;
    }

    // Only true 401s warrant a refresh; 400s are malformed requests, not
    // expired sessions, so fall through to the legacy redirect behaviour.
    const shouldAttemptRefresh =
      resStatus === 401 && process.browser && !(error.config && error.config.__isRetry);

    if (shouldAttemptRefresh) {
      try {
        const tokens = await doRefresh($axios, app);
        const tokenType = tokens.token_type || "Bearer";
        const retryConfig = {
          ...error.config,
          headers: {
            ...(error.config.headers || {}),
            Authorization: `${tokenType} ${tokens.access_token}`,
          },
          __isRetry: true,
        };
        return $axios.request(retryConfig);
      } catch (refreshErr) {
        // Surface so stuck-session bugs stop being invisible.
        console.error("[auth] token refresh failed", {
          status: refreshErr && refreshErr.response && refreshErr.response.status,
          message: refreshErr && refreshErr.message,
          data: refreshErr && refreshErr.response && refreshErr.response.data,
        });
        // fall through to redirect
      }
    }

    // In SSR, each request gets a fresh context, so the first redirect is
    // the one that wins — the module-level guard is only meaningful in the
    // browser where it prevents a 401 burst from queuing multiple pushes.
    if (!process.browser) {
      return redirect("/login");
    }

    if (!redirectingToLogin) {
      redirectingToLogin = true;
      try { await logout(app); } catch (e) { /* ignore */ }
      // Self-heal so later sessions in the same tab can redirect again.
      setTimeout(() => { redirectingToLogin = false; }, 5000);
      return redirect("/login");
    }
  });
}
